import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ReactComponent as UploadIcon } from "assets/images/iconsV2/upload-cloud-02.svg";
import Modal from "components/v2/Modal/Modal";
import Button from "components/v2/Button/Button";
import Checkbox from "components/v2/Checkbox/Checkbox";
import Divider from "components/v2/Divider/Divider";
import FilePreview from "components/v2/FilePreview/FilePreview";
import { createInvoiceBucketAttachment, patchInvoiceBucketAttachment } from "api";
import { classNames, formatFileSize } from "helpers/commonHelpers";
import errorToast from "helpers/errorToast";
import useDraggableUpload from "hooks/useDraggableUpload";
import {
  DocumentForSelection,
  INVOICE_ATTACHMENT_ACCEPT,
  InvoiceFormStop,
  GroupedBucketAttachment,
  buildDocumentForSelectionFromGroup,
  filterDeliveryStops,
  isDocumentForSelectionValid,
  selectEntireShipment,
  toggleStopSelection,
} from "./invoiceBucketAttachmentHelpers";
import "./InvoiceFormBucketAttachmentModal.scss";

type ModalMode = "add" | "edit";

type InvoiceFormBucketAttachmentModalProps = {
  show: boolean;
  mode: ModalMode;
  invoiceId: string;
  bucketId: number;
  shipmentStops: InvoiceFormStop[];
  editingAttachment?: GroupedBucketAttachment | null;
  onClose: () => void;
  onSuccess: () => void;
  onDelete?: (attachment: GroupedBucketAttachment) => Promise<boolean>;
};

const emptySelection = (): DocumentForSelection => ({
  entireShipment: false,
  stopIds: [],
});

function InvoiceFormBucketAttachmentModal({
  show,
  mode,
  invoiceId,
  bucketId,
  shipmentStops,
  editingAttachment,
  onClose,
  onSuccess,
  onDelete,
}: InvoiceFormBucketAttachmentModalProps) {
  const intl = useIntl();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [documentFor, setDocumentFor] = useState<DocumentForSelection>(emptySelection());
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!show) return;
    setStagedFiles([]);
    if (mode === "edit" && editingAttachment) {
      setDocumentFor(buildDocumentForSelectionFromGroup(editingAttachment));
      return;
    }
    setDocumentFor(emptySelection());
  }, [show, mode, editingAttachment]);

  const deliveryStops = useMemo(() => filterDeliveryStops(shipmentStops), [shipmentStops]);

  const isUploadEnabled = useMemo(() => {
    const hasValidDocumentFor = isDocumentForSelectionValid(documentFor);
    if (mode === "edit") return hasValidDocumentFor;
    return stagedFiles.length > 0 && hasValidDocumentFor;
  }, [documentFor, mode, stagedFiles.length]);

  const appendSelectedFiles = (files: FileList | File[] | null) => {
    if (!files) return;
    const selectedFiles = Array.from(files);
    if (!selectedFiles.length) return;
    setStagedFiles((previous) => [...previous, ...selectedFiles]);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    appendSelectedFiles(event.target.files);
    event.target.value = "";
  };

  const { dragging, ...draggableProps } = useDraggableUpload(appendSelectedFiles);

  const handleRemoveStagedFile = (index: number) => {
    setStagedFiles((previous) => previous.filter((_, fileIndex) => fileIndex !== index));
  };

  const handleDeleteEditingAttachment = async () => {
    if (!editingAttachment || !onDelete) return;
    const deleted = await onDelete(editingAttachment);
    if (deleted) {
      onClose();
    }
  };

  const appendAssociationFields = (formData: FormData) => {
    formData.append("invoice_attachment_bucket_pk", String(bucketId));
    formData.append("entire_shipment", documentFor.entireShipment ? "true" : "false");
    if (documentFor.stopIds.length) {
      formData.append("stop_ids", JSON.stringify(documentFor.stopIds));
    }
  };

  const handleSubmit = async () => {
    if (!isUploadEnabled) return;
    setIsSubmitting(true);

    try {
      if (mode === "edit" && editingAttachment) {
        await patchInvoiceBucketAttachment(invoiceId, editingAttachment.file.reference_number, {
          invoice_attachment_bucket_pk: bucketId,
          entire_shipment: documentFor.entireShipment,
          stop_ids: documentFor.stopIds,
        });
        onSuccess();
        onClose();
        return;
      }

      let uploadedCount = 0;
      const remaining = [...stagedFiles];
      // Upload sequentially so a mid-batch failure leaves remaining files staged.
      for (let index = 0; index < remaining.length; index += 1) {
        const file = remaining[index];
        try {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("filename", file.name);
          appendAssociationFields(formData);
          // eslint-disable-next-line no-await-in-loop
          await createInvoiceBucketAttachment(invoiceId, formData);
          uploadedCount += 1;
          setStagedFiles((previous) => previous.filter((staged) => staged !== file));
        } catch (error) {
          errorToast(error);
          if (uploadedCount > 0) {
            onSuccess();
          }
          return;
        }
      }

      onSuccess();
      onClose();
    } catch (error) {
      errorToast(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalTitle =
    mode === "edit"
      ? intl.formatMessage({
          id: "billing.invoiceAttachment.modal.editTitle",
          defaultMessage: "Edit attachment",
        })
      : intl.formatMessage({
          id: "billing.invoiceAttachment.modal.addTitle",
          defaultMessage: "Add attachment",
        });

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <Modal show={show} onHide={onClose} size="xl">
      <Modal.Header>
        <div className="text-primary text-xl font-semibold">{modalTitle}</div>
      </Modal.Header>
      <Modal.Body className="invoice-bucket-attachment-modal__body">
        <div className="invoice-bucket-attachment-modal">
          {mode === "add" && (
            <div className="invoice-bucket-attachment-modal__upload-section">
              <div
                className={classNames("invoice-bucket-attachment-modal__dropzone", {
                  "invoice-bucket-attachment-modal__dropzone--dragging": dragging,
                })}
                role="button"
                tabIndex={0}
                aria-label={intl.formatMessage({
                  id: "documentParser.uploadPage.uploadDropzone.clickToUpload",
                  defaultMessage: "Click to upload",
                })}
                onClick={openFileDialog}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openFileDialog();
                  }
                }}
                {...draggableProps}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="invoice-bucket-attachment-modal__dropzone-input"
                  accept={INVOICE_ATTACHMENT_ACCEPT}
                  multiple
                  onChange={handleFileChange}
                />
                <div className="invoice-bucket-attachment-modal__dropzone-inner">
                  <div className="invoice-bucket-attachment-modal__dropzone-icon">
                    <UploadIcon width={20} height={20} />
                  </div>
                  <p className="invoice-bucket-attachment-modal__dropzone-title">
                    <span className="invoice-bucket-attachment-modal__dropzone-title-action">
                      <FormattedMessage
                        id="documentParser.uploadPage.uploadDropzone.clickToUpload"
                        defaultMessage="Click to upload"
                      />
                    </span>{" "}
                    <FormattedMessage
                      id="documentParser.uploadPage.uploadDropzone.orDragAndDrop"
                      defaultMessage="or drag and drop"
                    />
                  </p>
                  <p className="invoice-bucket-attachment-modal__dropzone-subtitle">
                    <FormattedMessage
                      id="billing.invoiceAttachment.modal.uploadHelper"
                      defaultMessage="Supported formats: PDF, Word, Excel, PNG, JPEG, CSV (max 15 MB)"
                    />
                  </p>
                </div>
              </div>

              {!!stagedFiles.length && (
                <div className="invoice-bucket-attachment-modal__file-list">
                  {stagedFiles.map((file, index) => (
                    <FilePreview
                      key={`${file.name}-${file.size}-${index}`}
                      fileName={file.name}
                      fileSize={formatFileSize(file.size)}
                      onClose={() => handleRemoveStagedFile(index)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {mode === "edit" && editingAttachment && (
            <div className="invoice-bucket-attachment-modal__edit-file">
              <FilePreview
                fileName={editingAttachment.file.name}
                url={editingAttachment.file.url}
                onClose={onDelete ? handleDeleteEditingAttachment : undefined}
              />
            </div>
          )}

          <div className="invoice-bucket-attachment-modal__document-for">
            <div className="text-secondary-v2 text-sm font-medium">
              <FormattedMessage id="billing.invoiceAttachment.modal.documentFor" defaultMessage="Document for" />
            </div>

            <Checkbox
              checked={documentFor.entireShipment}
              onChange={() => setDocumentFor(selectEntireShipment())}
              label={
                <FormattedMessage id="billing.invoiceAttachment.entireShipment" defaultMessage="Entire shipment" />
              }
            />

            <Divider
              className="invoice-bucket-attachment-modal__divider"
              label={<FormattedMessage id="billing.invoiceAttachment.modal.orDivider" defaultMessage="or" />}
            />

            {deliveryStops.map((stop) => (
              <Checkbox
                key={stop.id}
                checked={!documentFor.entireShipment && documentFor.stopIds.includes(stop.id)}
                onChange={(event) =>
                  setDocumentFor(toggleStopSelection(documentFor, stop.id, event.target.checked))
                }
                label={intl.formatMessage(
                  {
                    id: "billing.invoiceAttachment.stopLabel",
                    defaultMessage: "Stop {sequences}",
                  },
                  { sequences: stop.sequence },
                )}
                supportingText={stop.full_address || undefined}
              />
            ))}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-end align-items-center w-100 gap-lg">
          <Button
            label={intl.formatMessage({ id: "general.cancel", defaultMessage: "Cancel" })}
            variant="tertiary-gray"
            onClick={onClose}
            disabled={isSubmitting}
          />
          <Button
            label={
              mode === "edit"
                ? intl.formatMessage({
                    id: "billing.invoiceAttachment.modal.save",
                    defaultMessage: "Save",
                  })
                : intl.formatMessage({
                    id: "billing.invoiceAttachment.modal.upload",
                    defaultMessage: "Upload",
                  })
            }
            onClick={handleSubmit}
            disabled={!isUploadEnabled || isSubmitting}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default InvoiceFormBucketAttachmentModal;
